import {
  HorseRacingEvent,
  HorseRacingMarket,
  EulerEvent,
} from "@virtuallysports/vs-utils";
import { Market } from "./Market";
import { Competitor } from "./Competitor";
import { ISubscribableEvent } from "./ISubscribableEvent";
import {
  EventDataEvents,
  EulerMessageStatuses,
  EulerStates,
  GameStates,
} from "./Consts";
import { EulerStateManager } from "./EulerStateManager";
import { StateManager } from "./GameStateManager";

export class EventData implements ISubscribableEvent {
  static instance: EventData | null = null;
  private eventData: HorseRacingEvent | null = null;
  private markets: Market[] = [];
  private competitors: Competitor[] = [];
  private arrival: number[] = [];
  private arrivalOrderFull: number[] = [];

  private dataAssigned: boolean = false;
  private eventCompletedMessageReceived: boolean = false;
  private arrivalOrderMessageReceived: boolean = false;
  private arrivalOrderEventTriggered: boolean = false;

  constructor() {}

  public static getInstance(): EventData {
    if (EventData.instance === null) {
      EventData.instance = new EventData();
    }
    return EventData.instance;
  }

  private onDataAssigned: (() => void)[] = [];
  private onArrivalOrderAssigned: (() => void)[] = [];
  private onCompletedMessageReceived: (() => void)[] = [];

  //accessor for eventCompletedMessageReceived
  public wasEventCompletedMessageReceived(): boolean {
    return this.eventCompletedMessageReceived;
  }

  public wasDataAssigned(): boolean {
    return this.dataAssigned;
  }

  public wasArrivalOrderMessageReceived(): boolean {
    return this.arrivalOrderMessageReceived;
  }

  public getEventData(): HorseRacingEvent | null {
    return this.eventData;
  }

  private setData(data: HorseRacingEvent, onSetData?: () => void): void {
    this.eventData = data;
    if (onSetData) {
      onSetData();
    }
  }

  public setArrival(arrival: number[]): void {
    this.arrivalOrderMessageReceived = true;
    this.arrival = arrival;
  }

  /*
  Gets the first 3 entries of the arrival order
   */
  public getArrival(): number[] {
    return this.arrival;
  }

  public getArrivalOrderFull(): number[] {
    return this.arrivalOrderFull;
  }

  public setArrivalOrderFull(arrivalFull: number[]) {
    this.arrivalOrderFull = arrivalFull;
  }

  public wasArrivalOrderAssigned(): boolean {
    const result = this.arrival.length > 0;
    return result;
  }

  public getArrivalAsString(): string {
    return this.arrival.join("");
  }

  public getStartTime(): string | null {
    return this.eventData?.startTime ?? null;
  }

  public getEventCompetitors(): HorseRacingEvent["competitors"] | null {
    return this.eventData?.competitors ?? null;
  }

  public getMarkets() {
    return this.markets;
  }

  public setMarkets(
    markets: Record<string, HorseRacingMarket>,
    onMarketsSet?: () => void,
  ) {
    const marketsArr: Market[] = [];
    const arr = Object.values(markets);

    for (let i = 0; i < arr.length; i++) {
      const market = arr[i];
      const newMarket = Market.createMarket(market);
      marketsArr.push(newMarket);
    }
    this.markets = marketsArr;
    if (onMarketsSet) {
      onMarketsSet();
    }
  }

  public getCompetitors(): Competitor[] {
    //return competitors sorted by cardinal number
    this.competitors.sort(
      (a, b) => a.getCompetitor().cardinal - b.getCompetitor().cardinal,
    );
    return this.competitors;
  }

  public findCompetitorById(id: string): Competitor | null {
    return this.competitors.find((c) => c.getCompetitor().id === id) ?? null;
  }

  public findCompetitorByName(name: string): Competitor | null {
    return (
      this.competitors.find((c) => c.getCompetitor().name === name) ?? null
    );
  }



  public setCompetitors(
    competitors: EulerEvent["competitors"],
    onCompetitorsSet?: () => void,
  ): void {
    const arr: Competitor[] = [];
    for (let i = 0; i < competitors.length; i++) {
      const { additionalParameters, id, name, cardinal, form } = competitors[i];

      arr.push(
        Competitor.createCompetitor({
          id,
          name,
          cardinal,
          form,
        }),
      );
    }
    this.competitors = arr;
    if (onCompetitorsSet) {
      onCompetitorsSet();
    }
  }

  public findMarketById(id: string) {
    return this.markets.find((market) => market.getId() === id);
  }

  public findMarketByBetcode(betcode: string) {
    return this.markets.find((market) => market.getBetcode() === betcode);
  }

  public findMarketByStatus(status: string) {
    return this.markets.find((market) => market.getStatus() === status);
  }

  public findMarketByType(type: string) {
    return this.markets.find((market) => market.getType() === type);
  }

  public getMainMarkets() {
    return this.markets.filter((market) => market.getIsMainMarket());
  }

  public async connectGamesocket(): Promise<void> {
    if (this.eventData) return;
    // Fetch current event ID
  }

  private validateStatus(currentState: string, status: string): boolean {
    //validate whether the status was expected while in this state

    if (status === EulerMessageStatuses.VOIDED) {
      //TODO - Handle voided message
    }

    switch (currentState) {
      case EulerStates.GET_CURRENT_EVENT_STATE:
        return status === EulerMessageStatuses.RUNNING;
      case EulerStates.GET_NEXT_EVENT_STATE:
        return (
          status === EulerMessageStatuses.PENDING ||
          status === EulerMessageStatuses.READY ||
          status === EulerMessageStatuses.END_BETS
        );
      case EulerStates.LOAD_AND_SHOW_ODDS_STATE:
        //IRRELEVANT as we dont care about messages in this state
        return status === EulerMessageStatuses.START; //TODO sometimes we receive the start message here before we have had time to transition to no more bets
      case EulerStates.NO_MORE_BETS_STATE:
        return status === EulerMessageStatuses.START;
      case EulerStates.UPDATE_PLAYING_STATE:
        return status === EulerMessageStatuses.RUNNING;
      case EulerStates.EVENT_PLAYING_STATE:
        return status === EulerMessageStatuses.COMPLETED;
      case EulerStates.EVENT_END_STATE:
        return false; //TODO handle this

      default:
        return false;
    }
  }

  public parseDataBasedOnState(data: EulerEvent): void {
    const currentState = EulerStateManager.getInstance();
    const currentStateID = currentState.getCurrentStateID();

    console.log("Current State ID: ", currentStateID);

    if (!currentStateID) {
      console.log("No current state ID");
      return;
    }
    const statusExpected = this.validateStatus(currentStateID, data.status);

    if (!statusExpected) {
      // TODO - Handle unexpected status
      return;
    }

    switch (currentStateID) {
      case EulerStates.GET_CURRENT_EVENT_STATE:
        this.assignDataForUpcomingEvent(data); //TODO - Assign for current event
        this.processArrivalOrder(data);

        // this.triggerDataAssignedEvent();
        break;
      case EulerStates.GET_NEXT_EVENT_STATE:
        this.assignDataForUpcomingEvent(data);
        break;
      case EulerStates.LOAD_AND_SHOW_ODDS_STATE:
        //Not waiting on any data here as we transition based on the timing reaching 0
        //this.processArrivalOrder(data);
        break;

      case EulerStates.NO_MORE_BETS_STATE:
        //this.processArrivalOrder(data); //While here, we wait for the arrival order
        break;

      case EulerStates.UPDATE_PLAYING_STATE:
        //this.processArrivalOrder(data);
        this.processRunningMessage(data);
        break;
      case EulerStates.EVENT_PLAYING_STATE:
        //this.processArrivalOrder(data);
        this.processMessageInEventPlayingState(data);
        break;
      case EulerStates.EVENT_END_STATE:
        break;
      default:
        break;
    }
  }

  /**
   * This assigns the data for the upcoming event
   * @param validHRData Data has been validated and is ready to be assigned
   * @param eventID  The ID of the event
   */
  private assignDataForUpcomingEvent(validHRData: any) {
    //TODO - Check the status of the event and handle accordingly
    console.log("Assigning data for upcoming event. DATA IS ", validHRData);

    const { competitors, markets } = validHRData;
    this.setData(validHRData);
    this.setMarkets(markets);
    this.setCompetitors(competitors);

    this.triggerDataAssignedEvent();
  }

  public processArrivalOrder(validHRData: any) {
    console.log("processArrivalOrder")
    const { results } = validHRData;
    this.setData(validHRData);

    if (typeof results != "string") {
      this.setArrival(results.arrival);
    } else {
      //We have received a message with a string and thats unexpected...
      return;
    }

    this.triggerArrivalOrderAssignedEvent();
  }

  public processArrivalOrderBackup(validHRData: any) {
    console.log("processArrivalOrder..")
    const { results } = validHRData;
    this.setData(validHRData);

    if (typeof results != "string") {
      this.setArrival(results.arrival);
    } else {
      //We have received a message with a string and thats unexpected...
      return;
    }

    this.triggerArrivalOrderAssignedEvent();
  }

  private processRunningMessage(validHRData: any) {}

  private processMessageInEventPlayingState(validHRData: any) {
    //TODO - Handle the completed event. Data has come in for the completed event so probably need to check if the race has actually completed (as its possible they tabbed out and are not up to date)

    const status = validHRData.status;
    if (status === EulerMessageStatuses.COMPLETED) {
      const currentGameState = StateManager.getInstance().getCurrentState()?.id;

      if (currentGameState === GameStates.RACE_IN_PROGRESS_STATE) {

        //reload the page to get the next event
        window.location.reload();
        return;

        this.eventCompletedMessageReceived = true;
      } else if (
        currentGameState === GameStates.POST_RACE_STATE ||
        currentGameState === GameStates.POST_EVENT_STATE
      ) {
        //just trigger the listeners array to fire and progress to the next event
        this.triggerCompleteMessageReceivedEvent();
      }
    } else if (status === EulerMessageStatuses.CLOSED) {
      //TODO - Handle the closed message (Most likely we dont care about this))
      console.debug("Closed message received");
    }
  }

  private triggerDataAssignedEvent(): void {
    this.onDataAssigned?.forEach((listener) => listener());
    this.dataAssigned = true;
  }

  private triggerArrivalOrderAssignedEvent(): void {
    if (this.arrivalOrderEventTriggered) {
      console.log("Arrival order event already triggered, skipping.");
      return;
    }

    console.log("Triggering arrival order assigned event");
    this.arrivalOrderEventTriggered = true;
    this.onArrivalOrderAssigned?.forEach((listener) => listener());
  }

  private triggerCompleteMessageReceivedEvent(): void {
    this.onCompletedMessageReceived?.forEach((listener) => listener());
  }

  public subscribeToEvent(eventName: string, listener: () => void): void {
    if (eventName === EventDataEvents.ON_DATA_ASSIGNED) {
      if (!this.onDataAssigned.includes(listener)) {
        this.onDataAssigned.push(listener);
        if (this.dataAssigned) {
          listener();
        }
      } else {
        console.log("Listener already exists in onDataAssigned array.");
      }
    } else if (eventName === EventDataEvents.ON_ARRIVAL_ORDER_ASSIGNED) {
      if (!this.onArrivalOrderAssigned.includes(listener)) {
        this.onArrivalOrderAssigned.push(listener);
      } else {
        console.log("Listener already exists in onArrivalOrderAssigned array.");
      }
    } else if (eventName === EventDataEvents.ON_COMPLETED_MESSAGE_RECEIVED) {
      if (!this.onCompletedMessageReceived.includes(listener)) {
        this.onCompletedMessageReceived.push(listener);
      } else {
        console.log(
          "Listener already exists in onCompletedMessageReceived array.",
        );
      }
    } else {
      throw new Error("Event name not recognized.");
    }
  }

  public unsubscribeFromEvent(eventName: string, listener: () => void): void {
    if (eventName === EventDataEvents.ON_DATA_ASSIGNED) {
      const index = this.onDataAssigned.indexOf(listener);
      if (index !== -1) {
        this.onDataAssigned.splice(index, 1);
      }
    } else if (eventName === EventDataEvents.ON_ARRIVAL_ORDER_ASSIGNED) {
      const index = this.onArrivalOrderAssigned.indexOf(listener);
      if (index !== -1) {
        this.onArrivalOrderAssigned.splice(index, 1);
      }
    } else if (eventName === EventDataEvents.ON_COMPLETED_MESSAGE_RECEIVED) {
      const index = this.onCompletedMessageReceived.indexOf(listener);
      if (index !== -1) {
        this.onCompletedMessageReceived.splice(index, 1);
      }
    } else {
      throw new Error("Event name not recognized.");
    }
  }
}
